/**
 * Fallback FX, EUR-based (units of currency per 1 EUR) — mirrors the live feed's
 * shape AND currency set (backend `api/v1/fx_rates/views.py::FX_CURRENCIES`),
 * plus the gap currencies the feed doesn't publish but the book still books in
 * (RUB, CNY). Used ONLY when the live feed is unavailable: approximate spot
 * levels, refreshed occasionally.
 *
 * Why an EUR vector instead of the old N×N matrix: the live path already
 * triangulates through EUR, so a single vector covers EVERY pair among these
 * currencies for free (no hand-maintained cross-rates that silently rot), and
 * keeps the offline currency set identical to the online one. Add a currency to
 * the backend FX_CURRENCIES list → mirror it here so an offline session covers
 * the same book.
 *
 * RUB note: TickDB has NO `EUR/RUB.CASH` symbol (verified 2026-06 — Western FX
 * feeds dropped RUB post-2022), so RUB is NEVER live: this fallback is its only
 * source. Keep the value roughly current. Do NOT "fix" this by adding RUB to the
 * backend FX_CURRENCIES list — a permanently-failing currency is never cached
 * (failed fetches retry by design), so it would re-hit TickDB on EVERY
 * `/fx-rates/` call and stall the endpoint up to the per-request timeout for
 * nothing. The materiality gate (see footer-totals) keeps a small RUB tail from
 * flagging ⚠; a material RUB book correctly flags it against this stale rate.
 */
import { eurRateOf, eurTriangulate } from './eur-triangulation';

const FALLBACK_EUR_RATES: Record<string, number> = {
  EUR: 1,
  // In the live feed (keep in sync with backend FX_CURRENCIES):
  USD: 1.16,
  GBP: 0.86,
  JPY: 186,
  CHF: 0.92,
  HKD: 9.09,
  AUD: 1.64,
  CAD: 1.62,
  SGD: 1.49,
  DKK: 7.47,
  CZK: 24.14,
  PLN: 4.24,
  SEK: 10.88,
  NOK: 11.03,
  TRY: 53.73,
  ZAR: 18.77,
  // Gap currencies — booked in the book, NOT published by the live feed:
  RUB: 95,
  CNY: 8.2,
};

// Currency list used when live rates are unavailable (drives offline dropdowns
// and getSupportedCurrencies). Derived from the fallback vector so the two never
// drift.
export const FALLBACK_CURRENCIES = Object.keys(FALLBACK_EUR_RATES);

export type SupportedCurrency = typeof FALLBACK_CURRENCIES[number] | (string & Record<never, never>);

// Module-level live rates (EUR-based), injected by useFxRatesSync hook
let _liveEurRates: Record<string, number> | null = null;

export const setLiveRates = (eurRates: Record<string, number> | null): void => {
  _liveEurRates = eurRates;
};

export const isUsingLiveRates = (): boolean => {
  return _liveEurRates !== null;
};

const getExchangeRateInternal = (from: string, to: string): number => {
  // EUR-based triangulation (eurTo / eurFrom), shared with the COB feed (review CU4): live first,
  // then the fallback vector — same math, so each covers every pair it lists. `undefined` (a leg
  // missing) falls through to the next source, then to a silent 1.0 (callers gate via …Source).
  const live = eurTriangulate(from, to, _liveEurRates);
  if (live !== undefined) return live;
  const fallback = eurTriangulate(from, to, FALLBACK_EUR_RATES);
  if (fallback !== undefined) return fallback;
  return 1.0;
};

export const getExchangeRate = (from: string, to: string): number => {
  return getExchangeRateInternal(from, to);
};

/**
 * Provenance of the rate `getExchangeRate(from, to)` would return:
 * - `'live'`     — covered by live EUR-based rates (or a trivial same-ccy pair);
 * - `'fallback'` — not in live rates but covered by the hardcoded table (stale);
 * - `'none'`     — unknown pair; `getExchangeRate` defaults to 1.0, which is a
 *                  silent mis-conversion. Callers rolling up a EUR/USD-equiv
 *                  total should flag `'fallback'` and `'none'` as approximate.
 */
export const getExchangeRateSource = (from: string, to: string): 'live' | 'fallback' | 'none' => {
  if (!from || !to) return 'none';
  const fromUpper = from.toUpperCase();
  const toUpper = to.toUpperCase();
  if (fromUpper === toUpper) return 'live';
  if (eurRateOf(fromUpper, _liveEurRates) && eurRateOf(toUpper, _liveEurRates)) return 'live';
  if (eurRateOf(fromUpper, FALLBACK_EUR_RATES) && eurRateOf(toUpper, FALLBACK_EUR_RATES)) return 'fallback';
  return 'none';
};

/**
 * The rate for a pair only when it comes from the live feed (or is a same-ccy
 * pair); `undefined` otherwise. A trade books at its fx_rate, so the booking
 * form must not auto-fill the stale fallback table or the 1.0 default.
 */
export const getLiveExchangeRate = (from: string, to: string): number | undefined =>
  getExchangeRateSource(from, to) === 'live' ? getExchangeRateInternal(from, to) : undefined;

export const getSupportedCurrencies = (extras: string[] = []): string[] => {
  // Always include the offline FALLBACK set — it carries the gap currencies (RUB/CNY)
  // the live feed never publishes but the book still books in (LR-474 r6). Keying the
  // picker off `Object.keys(_liveEurRates)` alone narrowed it to the 16 fed currencies
  // once live FX landed, making a foreign security UN-settleable in RUB/CNY from the
  // form even though getExchangeRate fully supports those pairs via this same fallback.
  // The live feed only ever ADDS to the offline superset; it must never shrink it.
  const base = _liveEurRates
    ? Array.from(new Set([...Object.keys(_liveEurRates), ...FALLBACK_CURRENCIES]))
    : [...FALLBACK_CURRENCIES];
  const extrasUpper = extras
    .filter((c): c is string => Boolean(c))
    .map((c) => c.toUpperCase());
  return Array.from(new Set([...base, ...extrasUpper])).sort();
};
